import { useState } from "react";
import PrimaryButton from "../../../components/Button/PrimaryButton";
import AuthField from "../../../components/Auth/AuthField";
import AuthSelect from "../../../components/Auth/AuthSelect";
import AuthCheckbox from "../../../components/Auth/AuthCheckbox";
import AuthBanner from "../../../components/Auth/AuthBanner";
import api from "../../../utils/axiosInstance";
import { useNavigate } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";
import LegalLinks from "../../../components/Legal/LegalLinks";
import { getCountries } from "../../../utils/constants/countries";
import { getTimezonesWithOffset } from "../../../utils/constants/timezones";
import { ENTERPRISE } from "../../../utils/constants/accountType";

const Section = ({ title, children }) => (
  <section className="space-y-4">
    <h2 className="text-xs font-bold uppercase tracking-wider text-gray-400">
      {title}
    </h2>
    {children}
  </section>
);

const SignUpForm = ({ accountType }) => {
  const navigate = useNavigate();
  const isAgency = accountType === ENTERPRISE;

  const [form, setForm] = useState({
    company_name: "",
    country: "",
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    time_zone: "",
    terms_accepted: false,
  });

  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState("");
  const [success, setSuccess] = useState("");
  const [captchaValue, setCaptchaValue] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const fieldError = (name, value) => {
    switch (name) {
      case "email":
        if (!value) return "Email is required.";
        return /\S+@\S+\.\S+/.test(value) ? "" : "Email is invalid.";
      case "company_name":
        return isAgency && !value ? "Company name is required." : "";
      case "country":
        return value ? "" : "Country is required.";
      case "time_zone":
        return value ? "" : "Time zone is required.";
      case "first_name":
        return value ? "" : "First name is required.";
      case "last_name":
        return value ? "" : "Last name is required.";
      case "phone":
        if (!value) return "Phone is required.";
        return /^\+?[0-9\s-]{7,15}$/.test(value) ? "" : "Phone number is invalid.";
      default:
        return "";
    }
  };

  const validate = () => {
    const currentErrors = {};

    Object.keys(form).forEach((name) => {
      const message = fieldError(name, form[name]);
      if (message) currentErrors[name] = message;
    });

    if (!captchaValue) currentErrors.captcha = "Please verify you are not a robot.";
    if (!form.terms_accepted) {
      currentErrors.terms_accepted = "You must accept the Privacy Policy and Terms.";
    }

    setErrors(currentErrors);
    return Object.keys(currentErrors).length === 0;
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setErrors((prev) => ({ ...prev, [name]: fieldError(name, value) }));
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: "" });
    setServerError("");
    setSuccess("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    try {
      const payload = {
        ...form,
        account_type: accountType,
        company_name: isAgency ? form.company_name : "",
      };
      const response = await api.post("/auth/signup", payload);
      setSuccess(response.data.message);
      setServerError("");
      setTimeout(() => {
        navigate("/login");
      }, 2000);
    } catch (err) {
      const data = err.response?.data;
      setSuccess("");
      setServerError(
        data?.error ||
          data?.errors?.[0]?.msg ||
          "Failed to register. Please try again."
      );
      setSubmitting(false);
    }
  };

  return (
    <form className="mt-8 space-y-7" onSubmit={handleSubmit} noValidate>
      <AuthBanner error={serverError} success={success} />

      <Section title="Account Details">
        {isAgency && (
          <AuthField
            name="company_name"
            label="Company Name"
            placeholder="Company Name"
            value={form.company_name}
            error={errors.company_name}
            onChange={handleChange}
            onBlur={handleBlur}
          />
        )}

        <AuthSelect
          name="country"
          label="Country"
          placeholder="Select a country..."
          options={getCountries()}
          value={form.country}
          error={errors.country}
          onChange={handleChange}
          onBlur={handleBlur}
        />
      </Section>

      <Section title="Account Owner Details">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <AuthField
            name="first_name"
            label="First Name"
            placeholder="First Name"
            value={form.first_name}
            error={errors.first_name}
            onChange={handleChange}
            onBlur={handleBlur}
          />
          <AuthField
            name="last_name"
            label="Last Name"
            placeholder="Last Name"
            value={form.last_name}
            error={errors.last_name}
            onChange={handleChange}
            onBlur={handleBlur}
          />
        </div>

        <AuthField
          name="email"
          label="Email"
          type="email"
          placeholder="Email"
          value={form.email}
          error={errors.email}
          onChange={handleChange}
          onBlur={handleBlur}
        />

        <AuthField
          name="phone"
          label="Phone"
          type="tel"
          placeholder="Phone"
          value={form.phone}
          error={errors.phone}
          onChange={handleChange}
          onBlur={handleBlur}
        />

        <AuthSelect
          name="time_zone"
          label="Time Zone"
          placeholder="Select a timezone..."
          options={getTimezonesWithOffset()}
          value={form.time_zone}
          error={errors.time_zone}
          onChange={handleChange}
          onBlur={handleBlur}
        />
      </Section>

      <div className="space-y-4">
        <div>
          <ReCAPTCHA
            sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
            onChange={(value) => {
              setCaptchaValue(value);
              setErrors((prev) => ({ ...prev, captcha: "" }));
            }}
          />
          {errors.captcha && (
            <p className="mt-1.5 text-xs text-danger-500">{errors.captcha}</p>
          )}
        </div>

        <AuthCheckbox
          name="terms_accepted"
          checked={form.terms_accepted}
          error={errors.terms_accepted}
          onChange={(e) => {
            setForm({ ...form, terms_accepted: e.target.checked });
            setErrors((prev) => ({ ...prev, terms_accepted: "" }));
          }}
        >
          I have read and accept the <LegalLinks />.
        </AuthCheckbox>
      </div>

      <PrimaryButton type="submit" disabled={submitting}>
        {submitting ? "Creating Account..." : "Sign Up"}
      </PrimaryButton>
    </form>
  );
};

export default SignUpForm;